<template>
  <v-navigation-drawer
    :mini-variant.sync="mini"
    v-model="inputValue"
    app
    dark
    floating
    class="clinical-sidebar no-print"
    persistent
    :permanent="isLargeScreen"
    mobile-breakpoint="1070"
    :width="260"
    :mini-variant-width="68"
  >
    <!-- 1. Header: Official Maria Logo on Spruce Teal Canvas -->
    <div
      class="sidebar-header d-flex align-center px-3"
      :class="{ 'justify-center': mini, 'justify-space-between': !mini }"
    >
      <!-- Full Mode: Official Maria Center Logo Centered -->
      <div
        v-if="!mini"
        class="sidebar-logo-brand d-flex align-center justify-center flex-grow-1 cursor-pointer mr-2"
        @click="$router.push('/').catch(() => {})"
        title="Maria Rehabilitation Center"
      >
        <img
          :src="require('@/assets/logo2.png')"
          alt="Maria Rehabilitation Center"
          class="sidebar-logo-img"
        />
      </div>

      <!-- Mini Mode: Official Maria Emblem Icon -->
      <div
        v-else
        class="sidebar-logo-mini cursor-pointer d-flex align-center justify-center"
        @click.stop="toggleMini"
        title="Expand Sidebar"
      >
        <img
          :src="require('@/assets/icon.png')"
          alt="Maria"
          class="sidebar-mini-icon-img"
        />
      </div>

      <!-- Collapse Toggle Button (Full Mode) -->
      <v-btn
        v-if="!mini"
        icon
        small
        class="collapse-toggle-btn flex-shrink-0"
        @click.stop="toggleMini"
        title="Collapse Sidebar"
      >
        <v-icon size="19" color="#2DD4BF">mdi-chevron-double-left</v-icon>
      </v-btn>
    </div>

    <!-- 2. Scrollable Navigation Menu -->
    <div class="sidebar-nav-container">
      <!-- Section: CORE MANAGEMENT -->
      <div class="nav-section-title" v-if="!mini">CORE MANAGEMENT</div>
      <v-divider v-if="mini" class="my-2 section-mini-divider"></v-divider>

      <v-list dense nav class="pa-0 clinical-nav-list">
        <template v-for="item in coreNavItems">
          <v-tooltip right :disabled="!mini" :key="item.title">
            <template v-slot:activator="{ on, attrs }">
              <v-list-item
                :to="item.route"
                :exact="item.route === '/'"
                active-class="nav-item-active"
                class="clinical-nav-item my-1"
                :class="{ 'mx-2': !mini, 'mini-item mx-auto': mini }"
                v-bind="attrs"
                v-on="on"
              >
                <v-list-item-icon class="my-auto" :class="{ 'mr-3': !mini, 'ma-0 justify-center': mini }">
                  <v-icon size="20" class="nav-item-icon">{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content v-if="!mini">
                  <v-list-item-title class="nav-item-title">{{ item.title }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="!mini && item.badge > 0" class="ma-0 my-auto">
                  <span
                    class="clinical-badge-pill"
                    :class="getBadgeClass(item.badgeType)"
                  >
                    {{ item.badge }}
                  </span>
                </v-list-item-action>
              </v-list-item>
            </template>
            <span>{{ item.title }}</span>
          </v-tooltip>
        </template>
      </v-list>

      <!-- Section: ADMINISTRATION -->
      <div class="nav-section-title mt-4" v-if="!mini && adminNavItems.length > 0">ADMINISTRATION</div>
      <v-divider v-if="mini && adminNavItems.length > 0" class="my-2 section-mini-divider"></v-divider>

      <v-list dense nav class="pa-0 clinical-nav-list" v-if="adminNavItems.length > 0">
        <template v-for="item in adminNavItems">
          <v-tooltip right :disabled="!mini" :key="item.title">
            <template v-slot:activator="{ on, attrs }">
              <v-list-item
                :to="item.route"
                active-class="nav-item-active"
                class="clinical-nav-item my-1"
                :class="{ 'mx-2': !mini, 'mini-item mx-auto': mini }"
                v-bind="attrs"
                v-on="on"
              >
                <v-list-item-icon class="my-auto" :class="{ 'mr-3': !mini, 'ma-0 justify-center': mini }">
                  <v-icon size="20" class="nav-item-icon">{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content v-if="!mini">
                  <v-list-item-title class="nav-item-title">{{ item.title }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <span>{{ item.title }}</span>
          </v-tooltip>
        </template>
      </v-list>
    </div>

    <!-- 3. Bottom User Profile Card (Strictly pinned at bottom) -->
    <template v-slot:append>
      <div class="sidebar-user-footer" :class="{ 'px-3': !mini, 'mini-footer': mini }">
        <!-- Full Mode Profile Card -->
        <div v-show="!mini" class="user-card-wrapper">
          <div class="d-flex align-center justify-space-between w-100 user-card-row">
            <!-- Avatar + Dynamic Full Name & Role -->
            <div class="d-flex align-center overflow-hidden mr-2">
              <div class="user-avatar-circle flex-shrink-0 mr-4">
                {{ userInitials }}
              </div>
              <div class="user-info-stack overflow-hidden">
                <div class="user-display-name text-truncate" :title="userDisplayName">
                  {{ userDisplayName }}
                </div>
                <div class="user-role-label text-truncate" :title="userRole">
                  {{ userRole }}
                </div>
              </div>
            </div>

            <!-- 3-Dots Action Menu (Positioned on the Right) -->
            <v-menu
              v-model="userMenuOpen"
              top
              left
              offset-y
              :nudge-top="8"
              :close-on-content-click="true"
              transition="slide-y-transition"
              content-class="user-action-popup"
              z-index="9999"
              min-width="170"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="user-menu-btn ml-auto flex-shrink-0"
                  v-bind="attrs"
                  v-on="on"
                  title="User Account Menu"
                >
                  <v-icon size="20" color="rgba(255, 255, 255, 0.75)">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-card class="user-popup-card rounded-lg" elevation="8" light>
                <v-list dense class="py-1">
                  <v-list-item link :to="{ name: 'EditCurrentUserProfile' }">
                    <v-list-item-icon class="mr-2 my-auto">
                      <v-icon small color="#0D9488">mdi-account-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="font-weight-medium">Profile</v-list-item-title>
                  </v-list-item>

                  <v-divider class="my-1"></v-divider>

                  <v-list-item link @click="logOut">
                    <v-list-item-icon class="mr-2 my-auto">
                      <v-icon small color="#EF4444">mdi-logout</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="font-weight-medium red--text text--accent-2">
                      Logout
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </div>
        </div>

        <!-- Mini Mode Profile Avatar -->
        <div v-show="mini" class="mini-avatar-wrapper">
          <div class="mini-avatar-container">
            <div
              class="user-avatar-circle cursor-pointer"
              :title="userDisplayName"
            >
              {{ userInitials }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from "vuex";
import api from "@/utils/api";
import apolloClient from "@/apollo/apollo";
import gql from "graphql-tag";

export default {
  name: "NavBar",
  data() {
    return {
      mini: false,
      userMenuOpen: false,
      fetchedProfile: null,
      isFetchingProfile: false,
      unreadMessagesCount: 0,
      pendingRequestsCount: 0,
      pendingPoliciesCount: 0,
      unreadInterval: null,
      pendingRequestsInterval: null,
      forcedPoliciesInterval: null,
    };
  },
  created() {
    window.addEventListener("localstorage-change", this.handleLocalStorageChange);
  },
  async mounted() {
    this.fetchLoggedUserProfile();
    this.fetchUnreadCount();
    this.fetchPendingRequestsCount();
    this.fetchPendingAndRejectedForcedPoliciesCount();

    // Polling intervals for real-time counters
    this.unreadInterval = setInterval(this.fetchUnreadCount, 10000);
    this.pendingRequestsInterval = setInterval(this.fetchPendingRequestsCount, 30000);
    this.forcedPoliciesInterval = setInterval(this.fetchPendingAndRejectedForcedPoliciesCount, 30000);
  },
  beforeDestroy() {
    window.removeEventListener("localstorage-change", this.handleLocalStorageChange);
    if (this.unreadInterval) clearInterval(this.unreadInterval);
    if (this.pendingRequestsInterval) clearInterval(this.pendingRequestsInterval);
    if (this.forcedPoliciesInterval) clearInterval(this.forcedPoliciesInterval);
  },
  computed: {
    ...mapGetters(["getDataFromRouterView", "getPermissions", "getDrawer", "getLoggedUser"]),
    isLargeScreen() {
      return this.$vuetify.breakpoint.lgAndUp;
    },
    inputValue: {
      get() {
        return this.$store.getters.getDrawer;
      },
      set(val) {
        this.$store.dispatch("setDrawerAction", val);
      },
    },
    coreNavItems() {
      return [
        {
          title: "Dashboard",
          icon: "mdi-view-dashboard-outline",
          route: "/",
          permission: "dash_board_page",
        },
        {
          title: "Patients",
          icon: "mdi-account-multiple-outline",
          route: "/patients",
          permission: "patients_page",
        },
        {
          title: "Appointments",
          icon: "mdi-calendar-check-outline",
          route: "/appointements",
          permission: "appointments_page",
        },
        {
          title: "Relatives",
          icon: "mdi-account-group-outline",
          route: "/relatives",
          permission: "relatives_page",
          badge: this.pendingPoliciesCount,
          badgeType: "dark",
        },
        {
          title: "Requests",
          icon: "mdi-clipboard-text-clock-outline",
          route: "/requests",
          permission: "patient_requests_page",
          badge: this.pendingRequestsCount,
          badgeType: "warning",
        },
        {
          title: "Chats",
          icon: "mdi-message-text-outline",
          route: "/conversations",
          permission: "dash_board_page",
          badge: this.unreadMessagesCount,
          badgeType: "teal",
        },
      ].filter((item) => this.checkPermission(item.permission));
    },
    adminNavItems() {
      return [
        {
          title: "Settings",
          icon: "mdi-cog-outline",
          route: "/settings",
          permission: "setting_page",
        },
        {
          title: "Statistics",
          icon: "mdi-chart-line",
          route: "/statistics",
          permission: "statistics_page",
        },
        {
          title: "Reports",
          icon: "mdi-file-chart-outline",
          route: "/reports",
          permission: "reports_page",
        },
      ].filter((item) => this.checkPermission(item.permission));
    },
    navItems() {
      return [...this.coreNavItems, ...this.adminNavItems];
    },
    filteredNavItems() {
      return this.navItems;
    },
    currentUser() {
      const storeUser = this.getLoggedUser;
      if (storeUser && storeUser !== "null" && typeof storeUser === "object") {
        return storeUser;
      }
      try {
        const raw = localStorage.getItem("user");
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },
    userDisplayName() {
      const user = this.fetchedProfile || this.currentUser;
      const firstName = user?.contact?.firstName;
      const lastName = user?.contact?.lastName;
      if (firstName && lastName) {
        return `${firstName} ${lastName}`.trim();
      }
      if (user?.contact?.fullName) {
        return user.contact.fullName;
      }
      if (firstName) {
        return firstName;
      }
      if (user?.name) {
        return user.name;
      }
      if (user?.username) {
        return user.username;
      }
      return "Clinical Staff";
    },
    userInitials() {
      const name = this.userDisplayName;
      if (!name || typeof name !== "string") return "CS";
      const cleaned = name.replace(/^(Dr\.|Mr\.|Mrs\.|Ms\.)\s*/i, "").trim();
      if (!cleaned) return "CS";
      const words = cleaned.split(/\s+/).filter((w) => w && w.length > 0);
      if (words.length === 0) return "CS";
      if (words.length === 1) {
        return words[0].slice(0, 2).toUpperCase();
      }
      const first = words[0][0] || "";
      const last = words[words.length - 1][0] || "";
      const result = (first + last).toUpperCase();
      return result.length > 0 ? result : "CS";
    },
    userRole() {
      const user = this.fetchedProfile || this.currentUser;

      // 1. Official roleObject.name from DB
      if (user?.roleObject?.name) {
        return user.roleObject.name;
      }

      // 2. Check role field (if not the generic STANDARD string)
      if (user?.role && user.role !== "STANDARD" && user.role !== "DOCTOR") {
        return this.formatRoleName(user.role);
      }

      // 3. Check localStorage 'roles'
      try {
        const roles = JSON.parse(localStorage.getItem("roles") || "[]");
        if (Array.isArray(roles) && roles.length > 0) {
          const firstRole = roles[0];
          const rName = typeof firstRole === "object" ? firstRole.name : firstRole;
          if (rName && rName !== "STANDARD" && rName !== "DOCTOR") {
            return this.formatRoleName(rName);
          }
        }
      } catch (e) {}

      // 4. If DOCTOR, display Doctor
      if (user?.userType === "DOCTOR") {
        return "Doctor";
      }

      // 5. If user has ADMINISTRATOR permission
      const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
      if (permissions.includes("ADMINISTRATOR")) {
        return "Administrator";
      }

      return "Clinical Staff";
    },
  },
  methods: {
    toggleMini() {
      this.mini = !this.mini;
    },
    formatRoleName(str) {
      if (!str) return "";
      return str
        .replace(/[_-]+/g, " ")
        .trim()
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    checkPermission(permission) {
      if (!permission) return true;
      if (Array.isArray(permission)) {
        return this.$canAny ? this.$canAny(permission) : true;
      }
      return this.$canAny ? this.$canAny([permission]) : true;
    },
    getBadgeClass(type) {
      if (type === "warning") return "badge-warning";
      if (type === "teal") return "badge-teal";
      return "badge-dark";
    },
    handleLocalStorageChange() {
      // Re-evaluate reactive permissions or UI on storage changes.
      // Do NOT call fetchLoggedUserProfile() here: doing so triggers a circular infinite loop.
      this.$forceUpdate();
    },
    logOut() {
      this.$store.dispatch("logout");
      this.$router.push({ name: "Login" });
    },
    editUser() {
      const id = this.currentUser?.id;
      if (id) {
        this.$router.push({ name: "EditUser", params: { id: +id } });
      }
    },
    async fetchLoggedUserProfile() {
      const user = this.currentUser;
      const id = user?.id;
      if (!id || this.isFetchingProfile) return;

      this.isFetchingProfile = true;
      try {
        const response = await apolloClient.query({
          query: gql`
            query getLoggedUserProfile($id: Int!) {
              findOneUserById(id: $id) {
                id
                contact {
                  firstName
                  lastName
                  fullName
                }
                roleObject {
                  id
                  name
                }
                role
                userType
              }
            }
          `,
          variables: { id: +id },
          context: {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("accessToken"),
            },
          },
          fetchPolicy: "network-only",
        });

        const profile = response.data?.findOneUserById;
        if (profile) {
          this.fetchedProfile = profile;
          try {
            const raw = localStorage.getItem("user");
            const local = raw ? JSON.parse(raw) : {};
            const merged = { ...local, ...profile };
            const newSerialized = JSON.stringify(merged);
            if (raw !== newSerialized) {
              localStorage.setItem("user", newSerialized);
            }
            this.$store.commit("setloggedUser", merged);
          } catch (e) {}
        }
      } catch (err) {
        console.error("Failed to fetch logged user profile:", err);
      } finally {
        this.isFetchingProfile = false;
      }
    },
    async fetchUnreadCount() {
      try {
        const res = await api.get("/api/conversations/unread-total");
        if (typeof res.data === "number") {
          this.unreadMessagesCount = res.data;
        }
      } catch (e) {
        // Silent fail for polling
      }
    },
    async fetchPendingRequestsCount() {
      try {
        const response = await apolloClient.query({
          query: gql`
            query pendingRequestsCount {
              pendingRequestsCount
            }
          `,
          context: {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("accessToken"),
            },
          },
          fetchPolicy: "network-only",
        });
        this.pendingRequestsCount = response.data?.pendingRequestsCount || 0;
      } catch (e) {
        // Silent fail for polling
      }
    },
    async fetchPendingAndRejectedForcedPoliciesCount() {
      try {
        const response = await apolloClient.query({
          query: gql`
            query pendingAndRejectedForcedPoliciesCount {
              pendingAndRejectedForcedPoliciesCount
            }
          `,
          context: {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("accessToken"),
            },
          },
          fetchPolicy: "network-only",
        });
        this.pendingPoliciesCount = response.data?.pendingAndRejectedForcedPoliciesCount || 0;
      } catch (e) {
        // Silent fail for polling
      }
    },
  },
  watch: {
    isLargeScreen(val) {
      if (val) {
        this.$store.dispatch("setDrawerAction", val);
      }
    },
    getPermissions() {
      this.$forceUpdate();
    },
    getLoggedUser() {
      this.$forceUpdate();
    },
  },
};
</script>

<style scoped>
@media print {
  .no-print {
    display: none !important;
  }
}

.clinical-sidebar,
.clinical-sidebar ::v-deep .v-navigation-drawer__content {
  background-color: #131B2E !important;
  border-right: 1px solid rgba(255, 255, 255, 0.08) !important;
  z-index: 20;
}

.clinical-nav-list {
  background-color: transparent !important;
}

/* Header */
.sidebar-header {
  height: 64px;
  background-color: #131B2E !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.sidebar-logo-brand {
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
}

.sidebar-logo-img {
  max-height: 44px;
  max-width: 175px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  filter: brightness(1.5) contrast(1);
  transition: transform 0.2s ease, filter 0.2s ease;
}

.sidebar-logo-brand:hover .sidebar-logo-img {
  transform: scale(1.02);
  filter: brightness(1.5) contrast(1);
}

.sidebar-logo-mini {
  user-select: none;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background-color 0.15s ease;
}

.sidebar-logo-mini:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.sidebar-mini-icon-img {
  max-height: 34px;
  max-width: 34px;
  object-fit: contain;
  display: block;
  filter: brightness(1.5) contrast(1);
  transition: transform 0.2s ease;
}

.sidebar-logo-mini:hover .sidebar-mini-icon-img {
  transform: scale(1.08);
}

.collapse-toggle-btn {
  border-radius: 6px;
  transition: background-color 0.15s ease;
}

.collapse-toggle-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

/* Scrollable navigation container */
.sidebar-nav-container {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 12px;
}

.sidebar-nav-container::-webkit-scrollbar {
  width: 4px;
}

.sidebar-nav-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 4px;
}

.section-mini-divider {
  border-color: rgba(255, 255, 255, 0.08) !important;
  margin: 8px 12px !important;
}

/* Section titles */
.nav-section-title {
  font-size: 10px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 0.8px;
  padding: 14px 18px 6px 18px;
  text-transform: uppercase;
}

/* Navigation items */
.clinical-nav-item {
  border-radius: 10px !important;
  min-height: 42px !important;
  margin-bottom: 4px !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  color: rgba(255, 255, 255, 0.72) !important;
}

.clinical-nav-item.mini-item {
  width: 44px !important;
  min-width: 44px !important;
  max-width: 44px !important;
  height: 44px !important;
  min-height: 44px !important;
  max-height: 44px !important;
  padding: 0 !important;
  margin: 4px auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 10px !important;
}

/* Perfect centering for icon in mini-variant drawer */
.clinical-nav-item.mini-item .v-list-item__icon,
.v-navigation-drawer--mini-variant .clinical-nav-item .v-list-item__icon {
  margin: 0 !important;
  padding: 0 !important;
  width: 100% !important;
  height: 100% !important;
  min-width: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  align-self: center !important;
}

.clinical-nav-item.mini-item .v-icon,
.v-navigation-drawer--mini-variant .clinical-nav-item .v-icon {
  margin: auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.clinical-nav-item:hover {
  background-color: rgba(255, 255, 255, 0.06) !important;
  color: #FFFFFF !important;
}

.clinical-nav-item:hover .nav-item-icon {
  color: #2DD4BF !important;
}

.clinical-nav-item.nav-item-active {
  background-color: rgba(13, 148, 136, 0.28) !important;
  border: 1px solid rgba(45, 212, 191, 0.3) !important;
  color: #FFFFFF !important;
}

.clinical-nav-item.nav-item-active .nav-item-icon {
  color: #2DD4BF !important;
}

.clinical-nav-item.nav-item-active .nav-item-title {
  font-weight: 700 !important;
  color: #FFFFFF !important;
}

.nav-item-icon {
  color: rgba(255, 255, 255, 0.55);
  transition: color 0.15s ease;
}

.nav-item-title {
  font-size: 13px !important;
  font-weight: 500;
  letter-spacing: normal;
}

/* Badges */
.clinical-badge-pill {
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1;
  padding: 3px 7px;
  border-radius: 9999px;
  white-space: nowrap;
}

.badge-dark {
  background: rgba(255, 255, 255, 0.12);
  color: #FFFFFF;
}

.badge-warning {
  background: #F59E0B;
  color: #FFFFFF;
  box-shadow: 0 1px 4px rgba(245, 158, 11, 0.4);
}

.badge-teal {
  background: #0D9488;
  color: #FFFFFF;
}

/* Pinned Append and User Footer */
::v-deep .v-navigation-drawer__append {
  position: absolute !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  z-index: 50 !important;
  background-color: #0F172A !important;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  overflow: visible !important;
}

::v-deep .v-navigation-drawer__content {
  padding-bottom: 70px !important;
}

.sidebar-user-footer {
  position: relative !important;
  background-color: #0F172A !important;
  border-top: 1px solid rgba(255, 255, 255, 0.08) !important;
  height: 64px !important;
  width: 100% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-sizing: border-box !important;
  overflow: visible !important;
}

.sidebar-user-footer.mini-footer {
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.user-card-wrapper {
  width: 100%;
}

.mini-avatar-wrapper {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mini-avatar-container {
  width: 100% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.mini-avatar-container .user-avatar-circle {
  margin: 0 auto !important;
}

.user-card-row {
  width: 100%;
}

.user-avatar-circle {
  width: 38px !important;
  height: 38px !important;
  min-width: 38px !important;
  min-height: 38px !important;
  border-radius: 50% !important;
  background-color: #0D9488 !important;
  color: #FFFFFF !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  user-select: none !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25) !important;
  cursor: pointer !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.cursor-pointer {
  cursor: pointer;
}

.user-info-stack {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.user-display-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #FFFFFF;
  line-height: 1.2;
}

.user-role-label {
  font-size: 11.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7) !important;
  line-height: 1.2;
  margin-top: 2px;
}

.user-menu-btn {
  border-radius: 6px;
  transition: background-color 0.15s ease;
}

.user-menu-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

/* Popup Menu Card */
.user-action-popup {
  z-index: 9999 !important;
}

.user-popup-card {
  background-color: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.25) !important;
}
</style>

<style>
/* Unscoped: ensure mini-variant drawer shows the append slot */
.clinical-sidebar.v-navigation-drawer--mini-variant .v-navigation-drawer__append {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  overflow: visible !important;
  width: 100% !important;
}
</style>
